import { useState } from 'react';
import { Group, MultiSelect, Paper, Stack, Text } from '@mantine/core';
import { SearchInput } from '../../../components';
import { useJobTasks } from '../../../queries/use-job-tasks';
import { useTableState } from '../../../hooks/use-table-state';
import { PAGE_SIZE } from '../../../magic-values';
import { JobTasksTable } from './job-tasks-table';
import { JobTaskDrawer } from './job-task-drawer';
import { JobTaskStatus, type JobTask } from '../../../types/job-task';
import type { JobTasksFilters } from '../../../api/job-task-service';

const JOB_TASK_STATUSES = Object.values(JobTaskStatus);

interface JobTasksSectionProps {
  accountId: string;
  jobId: string;
  refetchInterval?: number | false;
}

export function JobTasksSection({ accountId, jobId, refetchInterval }: JobTasksSectionProps) {
  const { page, sortBy, sortDir, sortStatus, setPage, setSortStatus, getFilter, setFilter } =
    useTableState();
  const status = getFilter('status');
  const type = getFilter('type');
  const [selectedTask, setSelectedTask] = useState<JobTask | null>(null);

  const filters: JobTasksFilters = { accountId, jobId };
  if (status) filters.status = status;
  if (type) filters.type = type;

  const { data, isLoading, isError, error } = useJobTasks(
    filters,
    { page, pageSize: PAGE_SIZE, sortBy, sortDir },
    refetchInterval,
  );
  const tasks = data?.items ?? [];

  return (
    <Stack spacing="sm">
      <Group spacing="sm">
        <MultiSelect
          data={JOB_TASK_STATUSES}
          value={status ? status.split(',') : []}
          onChange={(values) => setFilter('status', values.join(','))}
          placeholder="Status"
          clearable
          sx={{ minWidth: 220 }}
        />
        <SearchInput
          value={type}
          onChange={(value) => setFilter('type', value)}
          placeholder="Filter by exact type…"
        />
      </Group>
      {isError ? (
        <Text color="red">
          Failed to load tasks: {(error as Error)?.message ?? 'Unknown error'}
        </Text>
      ) : (
        <Paper withBorder sx={{ overflowX: 'auto' }}>
          <JobTasksTable
            tasks={tasks}
            fetching={isLoading}
            onRowClick={setSelectedTask}
            page={page}
            totalRecords={data?.total ?? 0}
            onPageChange={setPage}
            sortStatus={sortStatus}
            onSortStatusChange={setSortStatus}
            height="calc(100vh - 500px)"
          />
        </Paper>
      )}
      <JobTaskDrawer task={selectedTask} onClose={() => setSelectedTask(null)} />
    </Stack>
  );
}
